import { Box, Typography } from "@mui/material";
import { memo, type ReactNode } from "react";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";

type Props = {
	/** The single line the cell shows; ellipsized when the column is too narrow for it. */
	text: string;
	/** Extra detail revealed on hover (e.g. the email behind a first name). Omit or pass `null` when the
	 * hover would only repeat the cell — then no tooltip is mounted at all. */
	tooltip?: string | null;
	/** Lands on the text element. */
	dataTestId?: string;
	/** Trailing content after the text on the same line (e.g. a chip naming who the value acted for). It sits
	 * outside the text's tooltip; the text still ellipsizes first. Omit for a plain text cell. */
	endSlot?: ReactNode;
};

/**
 * One line of secondary grid text, vertically centred in a `SystemDataGrid` cell (MUI's plain-text
 * centring stops applying once a Typography sets its own line-height), with an optional hover detail.
 *
 * The tooltip DESCRIBES the text rather than naming it (`describeChild`), so a screen reader announces
 * the visible value first and the detail second — the default would replace the visible text with the
 * tooltip as the element's accessible name.
 *
 * Generic on purpose: any cell whose visible value has a longer or more exact form behind it (a first
 * name and its email, a short code and its full name) renders the same way. `endSlot` adds trailing
 * content on the same line without giving up the text's ellipsis.
 */
export const TooltipTextCell = memo(function TooltipTextCell({ text, tooltip, dataTestId, endSlot }: Props) {
	const content = (
		<Typography
			data-testid={dataTestId}
			sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
		>
			{text}
		</Typography>
	);
	return (
		// With an end slot the row spans the cell, so the slot's cap is the cell's width, not the content's: the slot
		// keeps its natural width and the text ellipsizes first; only a slot wider than the whole cell ellipsizes itself.
		// Without one the row stays content-sized, exactly as before.
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%", minWidth: 0, ...(endSlot ? { width: "100%" } : {}) }}>
			{tooltip ? (
				<InHouseTooltip title={tooltip} describeChild>
					{content}
				</InHouseTooltip>
			) : (
				content
			)}
			{endSlot && <Box sx={{ display: "flex", flexShrink: 0, maxWidth: "100%", minWidth: 0 }}>{endSlot}</Box>}
		</Box>
	);
});
